/**
 * The recruiter's home screen.
 *
 * This is the answer to the spreadsheet's biggest failing: 456 "call back" and
 * 141 "hold" promises that nothing ever resurfaced. Recall work leads, because
 * a followup nobody sees is the same as no followup at all.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  BadgeCheck,
  CalendarClock,
  CheckCircle2,
  Clock,
  Hourglass,
  Loader2,
  Phone,
  PhoneCall,
  Send,
  Wallet,
} from 'lucide-react';
import { toast } from 'sonner';
import { client, errorMessage } from '../api/client';
import { money } from '../lib/money';
import {
  EARNED_INCENTIVE_LABEL,
  TENTATIVE_HINT,
  TENTATIVE_INCENTIVE_LABEL,
  YET_TO_RECEIVE_LABEL,
} from '../lib/incentive';
import CallsTable from '../components/CallsTable';
import { EditCallDialog, FollowUpDialog } from '../components/CallDialogs';
import { OUTCOMES } from '../lib/callOutcomes';
import { formatPhone, telHref } from '../lib/phone';
import ShareLinks from '../components/ShareLinks';
import DashboardShell from '../components/DashboardShell';
import ShiftBar from '../components/ShiftBar';
import type { Call, CallOutcome, Followup, MyDay, Page } from '../types';

export default function MyDayPage() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<MyDay>({
    queryKey: ['my-day'],
    queryFn: async () => (await client.get('/api/v1/my-day')).data,
  });

  const complete = useMutation({
    mutationFn: async ({ id, note }: { id: string; note?: string }) =>
      (
        await client.post(`/api/v1/followups/${id}/complete`, {
          outcomeNote: note ?? null,
        })
      ).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-day'] });
      toast.success('Followup closed');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  /**
   * Recall, as the rows themselves rather than the promises about them.
   *
   * The endpoint the Daily Calls Report reads, narrowed by recall_only to the
   * calls this person's overdue chases point at. Same rows, same table, same
   * edit -- which is the whole reason this is a call query and not the
   * followup list that used to render here as cards.
   *
   * Keyed under ['calls'] deliberately: every call mutation in the
   * application already invalidates that prefix, so this list refreshes with
   * the report instead of each of them having to learn about the dashboard.
   */
  const recall = useQuery<Page<Call>>({
    queryKey: ['calls', 'recall'],
    queryFn: async () =>
      (
        await client.get('/api/v1/calls', {
          params: { recallOnly: true, mineOnly: false, limit: 200 },
        })
      ).data,
  });

  const [editing, setEditing] = useState<Call | null>(null);
  const [editFailure, setEditFailure] = useState<string | null>(null);
  const [followingUp, setFollowingUp] = useState<Call | null>(null);

  const edit = useMutation({
    mutationFn: async ({ call, changes }: { call: Call; changes: Record<string, unknown> }) =>
      (await client.patch(`/api/v1/calls/${call.id}`, changes)).data as Call,
    onSuccess: () => {
      // The four the report invalidates, plus this page's own counts. A
      // correction can move or close the chase, so the Recall list it was
      // made from is as likely to have changed as the row inside it.
      void queryClient.invalidateQueries({ queryKey: ['calls'] });
      void queryClient.invalidateQueries({ queryKey: ['candidates'] });
      void queryClient.invalidateQueries({ queryKey: ['still-to-call'] });
      void queryClient.invalidateQueries({ queryKey: ['alerts'] });
      void queryClient.invalidateQueries({ queryKey: ['my-day'] });
      setEditing(null);
      setEditFailure(null);
      toast.success('Call corrected');
    },
    onError: (e) => setEditFailure(errorMessage(e, 'Could not correct that call')),
  });

  const followUp = useMutation({
    // The same shape the report sends: everything but the outcome and the
    // remarks comes off the call being followed up, so the new row cannot
    // disagree with the one it came from.
    mutationFn: async ({
      call,
      outcome,
      remarks,
    }: {
      call: Call;
      outcome: CallOutcome;
      remarks: string;
    }) =>
      (
        await client.post('/api/v1/calls', {
          candidateId: call.candidateId,
          clientId: call.clientId,
          position: call.position,
          outcome,
          remarks: remarks.trim() || null,
        })
      ).data as Call,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['calls'] });
      void queryClient.invalidateQueries({ queryKey: ['candidates'] });
      // Ringing back is how a recall leaves this list: the new call resolves
      // the chase behind it, and may open another.
      void queryClient.invalidateQueries({ queryKey: ['my-day'] });
      setFollowingUp(null);
      toast.success('Follow-up call logged');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (!data) return null;

  return (
    /* The rail and the strip above it belong to this page, not to AppLayout.
       The shell hides its own header here and lets the page run full width;
       every other route keeps both, which is what leaves them untouched at
       every size. DashboardShell holds that arrangement, shared with the
       admin home. */
    <DashboardShell>
      {/* --- Page header -------------------------------------------------
          A title and one line of context, matching the dashboard reference.
          The reference also carries a tab strip and a filter row; both are
          deliberately absent. It has four dashboards behind those tabs and
          this is one, and every figure here is "mine, today" -- there is no
          date range, no owner and no status to filter by, so a filter bar
          would be decoration on a screen that has none elsewhere. */}
      <div>
        <h1 className="text-xl font-semibold text-gray-900">My Day</h1>
        <p className="mt-0.5 text-sm text-gray-500">
          Everything waiting on you today, and what it is worth.
        </p>
      </div>

      {/* --- Shift ---------------------------------------------------------
          ShiftBar is rendered untouched and unwrapped in any second card: it
          brings its own shell, and AttendancePage renders the same component.
          Restyling it there would have followed it onto that page, so the
          dashboard positions it and changes nothing about it. */}
      <ShiftBar />

      {/* --- The counters --------------------------------------------------
          One row, coloured icon chips, as the reference has them. Same four
          figures and the same danger tone on Recall. */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat
          label="Recall"
          value={data.overdueCount}
          icon={AlertCircle}
          tone={data.overdueCount > 0 ? 'danger' : 'neutral'}
        />
        <Stat label="Due today" value={data.dueTodayCount} icon={CalendarClock} />
        <Stat label="Calls today" value={data.callsToday} icon={PhoneCall} />
        <Stat label="Submissions today" value={data.submissionsToday} icon={Send} />
      </div>

      {/* --- Recall, and what the work is worth ----------------------------
          The reference's wide-panel-plus-narrow-panel band. Recall takes two
          columns because it is a table with eight of them and a third of the
          width would make it scroll sideways for no reason; the earnings
          panel takes one, the way "This Month" does there. */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Panel
          className="xl:col-span-2"
          title="Recall"
          hint="Promised and past their date. Correcting a row here is the same edit as correcting it on the Daily Calls Report — one record, read in two places."
        >
          {recall.isLoading ? (
            <div className="flex justify-center py-10 text-gray-400">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : (
            /* CallsTable unchanged, for the same reason as ShiftBar: the
               Daily Calls Report renders it and must not move. The panel
               supplies the heading and the scroll container around it. */
            <CallsTable
              rows={recall.data?.items ?? []}
              /* Due rather than Date: the call already happened, and what
                 makes the row urgent is how long the promise about it has
                 been out. */
              dateColumn="due"
              showPosition={false}
              busy={followUp.isPending}
              emptyMessage="Nothing overdue. Every promise is still in date."
              onFollowUp={setFollowingUp}
              onEdit={(row) => {
                setEditFailure(null);
                setEditing(row);
              }}
            />
          )}
        </Panel>

        {/* What the recruiter is actually working for.

            Three figures rather than two: "earned" and "yet to receive"
            diverge the moment anything is paid, and the gap is the one
            somebody asks about. Tentative carries its condition, because
            "why is this not earned yet" is the question the number provokes
            and leaving it unanswered is how a provisional figure gets read
            as a promise. */}
        <Panel title="Incentives" hint="Four percent of the agreed fee on your placements.">
          <div className="space-y-3">
            <MoneyStat
              label={TENTATIVE_INCENTIVE_LABEL}
              amount={data.incentiveTentative}
              hint={TENTATIVE_HINT}
              icon={Hourglass}
            />
            <MoneyStat
              label={EARNED_INCENTIVE_LABEL}
              amount={data.incentiveEarned}
              icon={BadgeCheck}
            />
            <MoneyStat
              label={YET_TO_RECEIVE_LABEL}
              amount={data.incentiveYetToReceive}
              hint="Earned and not yet paid."
              icon={Wallet}
              emphasis
            />
          </div>
        </Panel>
      </div>

      {/* --- Promises, and the start-of-day shortcuts ----------------------
          Share a Posting sits where Recent Activity does in the reference:
          right-hand column, below the fold of the main work. It is a
          start-of-day thing and not a figure about today. */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <div className="space-y-4 xl:col-span-2">
          <FollowupSection
            title="Due Today"
            items={data.dueToday}
            onComplete={(id, note) => complete.mutate({ id, note })}
            pending={complete.isPending}
          />

          <FollowupSection
            title="Coming Up"
            items={data.upcoming}
            collapsedByDefault
            onComplete={(id, note) => complete.mutate({ id, note })}
            pending={complete.isPending}
          />
        </div>

        <ShareLinks />
      </div>

      {editing && (
        <EditCallDialog
          call={editing}
          outcomes={OUTCOMES}
          saving={edit.isPending}
          failure={editFailure}
          /* No take-over from here. It moves work between people, which is a
             decision for the report where the recruiter column is visible;
             every row on this list is the reader's own already. */
          taking={false}
          onTake={() => {}}
          onCancel={() => {
            setEditing(null);
            setEditFailure(null);
          }}
          onSave={(changes) => edit.mutate({ call: editing, changes })}
        />
      )}

      {followingUp && (
        <FollowUpDialog
          // Keyed by call, so the outcome belongs to the row it was opened on
          // rather than carrying over from the last one.
          key={followingUp.id}
          call={followingUp}
          outcomes={OUTCOMES}
          submitting={followUp.isPending}
          onCancel={() => setFollowingUp(null)}
          onConfirm={(outcome, remarks) =>
            followUp.mutate({ call: followingUp, outcome, remarks })
          }
        />
      )}
    </DashboardShell>
  );
}

/**
 * A money tile.
 *
 * Its own component rather than widening Stat, which takes a count and
 * colours it red when it is above zero -- the opposite of what an
 * incentive figure wants. Zero here is shown rather than blanked: on the
 * tracker a column of zeroes is noise, but this tile *is* the answer to
 * "what am I owed", and an empty one would read as broken.
 */
/**
 * A titled card, which is what the dashboard reference is built from.
 *
 * Local to this page on purpose. The instinct is to promote it to
 * components/ and use it everywhere, and that is exactly how a redesign of
 * one screen leaks onto the other twenty — the brief here was that every
 * other page keeps its current layout.
 *
 * The body is not padded on the sides when it holds a table: CallsTable
 * brings its own ring and rounding, and a card inside a card reads as a
 * mistake.
 */
function Panel({
  title,
  hint,
  children,
  className = '',
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section
      className={`flex flex-col rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-200
                  transition-shadow duration-300 ease-out hover:shadow-md ${className}`}
    >
      <div className="mb-3">
        <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
        {hint && <p className="mt-0.5 text-xs text-gray-500">{hint}</p>}
      </div>
      <div className="min-w-0 flex-1">{children}</div>
    </section>
  );
}

function MoneyStat({
  label,
  amount,
  icon: Icon,
  hint,
  emphasis = false,
}: {
  label: string;
  amount: string;
  icon: typeof Clock;
  hint?: string;
  emphasis?: boolean;
}) {
  return (
    /* Stacked inside the Incentives panel rather than standing as its own
       card, so it does not read as a card inside a card. The ring is gone
       and a tinted ground carries the emphasis instead. */
    <div
      className={`rounded-lg p-3 transition-colors duration-300 ease-out ${
        emphasis
          ? 'bg-indigo-50/70 hover:bg-indigo-50'
          : 'bg-gray-50 hover:bg-gray-100'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-xs font-medium text-gray-600">{label}</p>
          <p
            className={`mt-1 text-2xl font-semibold tabular-nums ${
              emphasis ? 'text-indigo-700' : 'text-gray-900'
            }`}
          >
            {money(amount)}
          </p>
        </div>
        <Icon
          className={`mt-0.5 h-4 w-4 shrink-0 ${
            emphasis ? 'text-indigo-500' : 'text-gray-400'
          }`}
        />
      </div>
      {hint && <p className="mt-1 text-[11px] leading-snug text-gray-400">{hint}</p>}
    </div>
  );
}

function Stat({
  label,
  value,
  icon: Icon,
  tone = 'neutral',
}: {
  label: string;
  value: number;
  icon: typeof Clock;
  tone?: 'neutral' | 'danger';
}) {
  const alarmed = tone === 'danger' && value > 0;
  return (
    <div
      /* The shadow does the lifting. A real -translate-y would shift the
         card out from under a pointer resting near its bottom edge, which
         drops the hover, which returns the card -- the same oscillation the
         rail rows had. Geometry stays put; only the shadow and the ring
         move. */
      className="group rounded-xl bg-white p-4 ring-1 ring-gray-200
                 transition-shadow duration-300 ease-out hover:shadow-lg
                 hover:shadow-gray-300/50 hover:ring-gray-300"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm text-gray-500">{label}</p>
          <p
            className={`mt-2 text-3xl font-semibold tabular-nums ${
              alarmed ? 'text-red-600' : 'text-gray-900'
            }`}
          >
            {value}
          </p>
        </div>
        {/* The chip, which is what makes the reference read as a dashboard
            rather than a list of numbers. Coloured by state, not decoration:
            a Recall above zero is the one tile somebody has to act on. */}
        <span
          className={`grid h-10 w-10 shrink-0 place-items-center rounded-lg
            transition-transform duration-300 ease-out motion-safe:group-hover:scale-110 ${
            alarmed ? 'bg-red-50 text-red-600' : 'bg-indigo-50 text-indigo-600'
          }`}
        >
          <Icon className="h-5 w-5" />
        </span>
      </div>
    </div>
  );
}

function FollowupSection({
  title,
  description,
  items,
  tone = 'neutral',
  collapsedByDefault = false,
  onComplete,
  pending,
}: {
  title: string;
  description?: string;
  items: Followup[];
  tone?: 'neutral' | 'danger';
  collapsedByDefault?: boolean;
  onComplete: (id: string, note?: string) => void;
  pending: boolean;
}) {
  const [expanded, setExpanded] = useState(!collapsedByDefault);

  if (items.length === 0) {
    return (
      <section>
        <h2 className="mb-2 text-sm font-semibold text-gray-900">{title}</h2>
        <div className="card flex items-center gap-2 text-sm text-gray-500">
          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
          Nothing here.
        </div>
      </section>
    );
  }

  return (
    <section>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="mb-2 flex w-full items-center justify-between text-left"
      >
        <div>
          <h2 className="text-sm font-semibold text-gray-900">
            {title}{' '}
            <span
              className={`badge ml-1 ${
                tone === 'danger'
                  ? 'bg-red-100 text-red-700'
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              {items.length}
            </span>
          </h2>
          {description && <p className="mt-0.5 text-xs text-gray-500">{description}</p>}
        </div>
        <span className="text-xs text-gray-400">{expanded ? 'Hide' : 'Show'}</span>
      </button>

      {expanded && (
        <ul className="divide-y divide-gray-100 overflow-hidden rounded-xl bg-white ring-1 ring-gray-200">
          {items.map((item) => (
            <FollowupRow
              key={item.id}
              item={item}
              onComplete={onComplete}
              pending={pending}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function FollowupRow({
  item,
  onComplete,
  pending,
}: {
  item: Followup;
  onComplete: (id: string, note?: string) => void;
  pending: boolean;
}) {
  const [note, setNote] = useState('');
  const [open, setOpen] = useState(false);

  return (
    <li className="p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-medium text-gray-900">
              {item.candidateName ?? 'Unknown candidate'}
            </p>
            {item.candidatePhone && (
              <a
                href={`tel:${telHref(item.candidatePhone)}`}
                className="inline-flex items-center gap-1 whitespace-nowrap text-sm text-indigo-600 hover:underline"
              >
                <Phone className="h-3.5 w-3.5" />
                {formatPhone(item.candidatePhone)}
              </a>
            )}
            {item.isOverdue && (
              <span className="badge bg-red-100 text-red-700">Recall</span>
            )}
            {item.autoCreated && (
              <span className="badge bg-gray-100 text-gray-600" title="Created automatically from a call outcome">
                auto
              </span>
            )}
          </div>

          {item.note && (
            <p className="mt-1 line-clamp-2 text-sm text-gray-600">{item.note}</p>
          )}
          <p className="mt-1 text-xs text-gray-400">
            Due {new Date(item.dueAt).toLocaleString('en-IN', {
              day: '2-digit',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </p>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            className="btn-ghost text-sm"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? 'Cancel' : 'Close'}
          </button>
        </div>
      </div>

      {open && (
        <div className="mt-3 flex gap-2">
          <input
            spellCheck={true}
            className="input"
            placeholder="What happened? (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={2000}
          />
          <button
            type="button"
            className="btn-primary shrink-0"
            disabled={pending}
            onClick={() => {
              onComplete(item.id, note.trim() || undefined);
              setOpen(false);
              setNote('');
            }}
          >
            {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Done'}
          </button>
        </div>
      )}
    </li>
  );
}
